<template>
    <div class="global-quicksearch">
        <global-button v-if="!showSearch" icon="search" theme="secondary" @click="showSearch = true" :aria-label="$t('search.buttons.search')"/>
        <global-button v-if="showSearch" icon="close" theme="primary" @click="closeSearch" aria-label="close" />
        <div class="global-quicksearch__expanded" v-if="showSearch">
            <div class="global-quicksearch__search-container">
                <div class="global-quicksearch__search">
                    <label class="global-quicksearch__label" for="quicksearch-input">
                        <global-button icon="search" theme="ghost" :aria-label="$t('search.buttons.search')" />
                    </label>
                    <input ref="inputfield" autocomplete="off" v-model="keyword" type="text" id="quicksearch-input" class="global-quicksearch__input form__input"/>
                </div>

                <template v-if="(results && totalResults > 0 && Object.keys(results).length)">
                    <global-quicksearch-suggestions v-if="results.suggestions && results.suggestions.length"
                                                    :suggestions="results.suggestions" :keyword="keyword"
                                                    @selected="selectSuggestion"/>

                    <global-quicksearch-shortcut v-for="(shortcut, key) in results.shortcuts" :key="key" :shortcut="shortcut"/>

                    <global-quicksearch-result v-if="results.boards && results.boards.count"
                                               :result="results.boards"
                                               :title="$t('search.categories.boards')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=boards': undefined"
                    />
                    <global-quicksearch-result v-if="results.decor && results.decor.count"
                                               :result="results.decor"
                                               :title="$t('search.categories.decors')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=decor': undefined"
                    />
                    <global-quicksearch-result v-if="results.structure && results.structure.count"
                                               :result="results.structure"
                                               :title="$t('search.categories.structures')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=structure': undefined"
                    />
                    <global-quicksearch-result v-if="results.reference && results.reference.count"
                                               :result="results.reference"
                                               :title="$t('search.categories.references')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=reference': undefined"
                    />
                    <global-quicksearch-result v-if="results.downloads && results.downloads.count"
                                               :result="results.downloads"
                                               :title="$t('search.categories.downloads')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=downloads': undefined"
                    />
                    <global-quicksearch-result v-if="results.page && results.page.count"
                                               :result="results.page"
                                               :title="$t('search.categories.pages')"
                                               :resultsPageUrl="resultsPageUrl.length ? resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=page': undefined"
                    />
                    <a class="global-quicksearch__link-all" v-if="resultsPageUrl.length" :href="resultsPageUrl + '?tx_salbase9_search[q]=' + keyword + '&tx_salbase9_search[cat]=all'">{{ $t('search.buttons.showAll') }} ({{ totalResults }})</a>
                </template>
                <template v-else-if="results && results.shortcuts.length">
                    <global-quicksearch-shortcut v-for="(shortcut, key) in results.shortcuts" :key="key" :shortcut="shortcut"/>
                </template>

                <template v-if="totalResults === 0">
                    <p>
                        {{ $t('search.errors.noResult') }}
                    </p>
                </template>

            </div>
        </div>
    </div>
</template>

<script>
import GlobalButton from "@global/global-elements/global-button/global-button";
import GlobalQuicksearchResult from "@global/global-elements/global-quicksearch/components/global-quicksearch-result/global-quicksearch-result";
import GlobalQuicksearchSuggestions from "@global/global-elements/global-quicksearch/components/global-quicksearch-suggestions/global-quicksearch-suggestions";
import GlobalQuicksearchShortcut from "@global/global-elements/global-quicksearch/components/global-quicksearch-shortcut/global-quicksearch-shortcut";
import {overlay} from "@utilities/sal-overlay/sal-overlay";
import {track_abstract, tracking_events} from '@utilities/track-abstract/track-abstract';

export default {
    name: "global-quicksearch",
    components: {GlobalQuicksearchShortcut, GlobalQuicksearchSuggestions, GlobalQuicksearchResult, GlobalButton},
    props: ['api', 'resultsPageUrl', 'parameter'],
    data() {
        return {
            showSearch: false,
            keyword: '',
            timer: null,
            results: undefined,
            searchApi: '',
            controller: null
        }
    },
    watch: {
        keyword() {
            this.clearTimer()
            this.setTimer(this.search)
        },
        showSearch(newValue) {
            if (newValue) {
                overlay.showOverlay(overlay.options.stacking.behindMenu, 'globalQuicksearch', true, false)
                this.$nextTick(() => {
                    if (this.$refs.inputfield) {
                        this.$refs.inputfield.focus();
                    }
                });
            } else {
                overlay.hideOverlay('globalQuicksearch')
            }
        }
    },
    computed: {
        totalResults() {
            if (!this.results) {
                return ''
            }
            let total = 0

            Object.values(this.results).forEach(section => {
                if (section.count) {
                    total += section.count
                }
            })
            return total
        }
    },
    methods: {
        search() {
            if (this.controller) {
                this.controller.abort();
            }
            if (this.keyword === '') {
                return
            }

            if (Object.keys(this.parameter).length) {
                const url = new URL(document.location.origin + this.api)
                if (this.parameter.keyword) {
                    url.searchParams.set(this.parameter.keyword, this.keyword);
                }

                if (this.parameter.limitToCategories) {
                    url.searchParams.set('tx_salbase9_search[cat]', this.parameter.limitToCategories)
                }

                this.searchApi = url.toString()
            } else {
                this.searchApi = this.api
            }

            this.controller = new AbortController();
            fetch(this.searchApi, {signal: this.controller.signal})
                .then(response => {
                    if (response.status === 200) {
                        response.json().then(result => {
                            this.results = result
                            track_abstract.trigger_event(tracking_events.SearchQuicksearch, this.keyword);
                        })
                    }
                })
                .catch(err => {
                    if (err.name == 'AbortError') return;

                    this.results = undefined;
                    console.error(err)
                })
        },
        selectSuggestion(e) {
            this.results = undefined
            this.keyword = e.detail
        },
        setTimer(callback) {
            this.timer = setTimeout(callback, 250)
        },
        clearTimer() {
            if (this.timer) {
                clearTimeout(this.timer)
            }
        },
        closeSearch() {
            this.keyword = ''
            this.results = undefined
            this.clearTimer()
            this.showSearch = false
        },
        clickOutsideSearch(e) {
            if (!e.target.closest('.global-quicksearch')) {
                this.closeSearch()
            }
        }
    },
    mounted() {
        overlay.addEvent('click', this.clickOutsideSearch)
    }
}
</script>
